import React, { useState, useEffect } from 'react';
import { MealItem } from '../api';

interface MealFormProps {
  item?: MealItem;
  defaultDate?: string;
  onSave: (item: MealItem | Omit<MealItem, 'id'>) => void;
  onCancel: () => void;
}

const MealForm: React.FC<MealFormProps> = ({ item, defaultDate, onSave, onCancel }) => {
  const [foodName, setFoodName] = useState(item?.food_name || '');
  const [qty, setQty] = useState(item?.qty?.toString() || '1');
  const [unit, setUnit] = useState(item?.unit || '');
  const [notes, setNotes] = useState(item?.notes || '');
  const [carbs, setCarbs] = useState(item?.carbs?.toString() || '0');
  const [protein, setProtein] = useState(item?.protein?.toString() || '0');
  const [fat, setFat] = useState(item?.fat?.toString() || '0');
  const [calories, setCalories] = useState(item?.calories?.toString() || '0');
  const [mealTime, setMealTime] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (item?.meal_time) {
      // Convert ISO string to local datetime-local input format
      const date = new Date(item.meal_time);
      const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setMealTime(localDateTime);
    } else if (defaultDate) {
      // Set to noon on the default date
      setMealTime(`${defaultDate}T12:00`);
    }
  }, [item, defaultDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!foodName.trim()) {
      alert('Food name is required');
      return;
    }

    setIsSaving(true);

    // Convert local datetime to ISO string
    const mealTimeISO = new Date(mealTime).toISOString();

    const mealData = {
      food_name: foodName.trim(),
      qty: parseFloat(qty) || undefined,
      unit: unit.trim() || undefined,
      notes: notes.trim() || undefined,
      carbs: parseFloat(carbs) || 0,
      protein: parseFloat(protein) || 0,
      fat: parseFloat(fat) || 0,
      calories: parseFloat(calories) || 0,
      meal_time: mealTimeISO,
    };

    if (item) {
      onSave({ ...mealData, id: item.id } as MealItem);
    } else {
      onSave(mealData);
    }

    setIsSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Food Name *
          </label>
          <input
            type="text"
            value={foodName}
            onChange={(e) => setFoodName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Chicken breast"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Quantity
          </label>
          <input
            type="number"
            step="0.1"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="1"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unit
          </label>
          <input
            type="text"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., oz, cup, serving"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Meal Time
          </label>
          <input
            type="datetime-local"
            value={mealTime}
            onChange={(e) => setMealTime(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Optional notes"
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Macros</h4>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-blue-600 mb-1">Carbs (g)</label>
            <input
              type="number"
              step="0.1"
              value={carbs}
              onChange={(e) => setCarbs(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-green-600 mb-1">Protein (g)</label>
            <input
              type="number"
              step="0.1"
              value={protein}
              onChange={(e) => setProtein(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-amber-600 mb-1">Fat (g)</label>
            <input
              type="number"
              step="0.1"
              value={fat}
              onChange={(e) => setFat(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-purple-600 mb-1">Calories</label>
            <input
              type="number"
              step="1"
              value={calories}
              onChange={(e) => setCalories(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Leave macros as 0 to auto-calculate based on food name
        </p>
      </div>

      <div className="flex justify-end space-x-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
        >
          {isSaving ? 'Saving...' : item ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  );
};

export default MealForm;
